import { useEffect, useRef } from "react";
import { sanitizeCtaSource } from "../../helpers/ctaSource";
import { safeNextPath } from "../../helpers/safeNext";
import { trackSignupCtaClicked, trackSignupCtaViewed } from "../../helpers/analytics";

/**
 * Encart de teasing commun, jumeau React de <twig:Tease> (templates/components/Tease.html.twig,
 * bibliothèque de composants « Encart de teasing ») pour les îlots des pages Twig : même
 * balisage et mêmes classes globales (km.css). Montre la forme d'une section réservée,
 * jamais sa valeur : aucune donnée réservée n'est passée au composant.
 *
 * Différence avec le Twig : pas de data-auth sur l'invitation, l'îlot sait déjà si le
 * visiteur doit voir l'encart (le connecté ne le voit jamais, la section complète le remplace).
 */
export type KmTeaseProps = {
  /** cta_source de l'emplacement (identifiant court, a-z 0-9 _ -). */
  source: string;
  title: string;
  /** Ce que le compte apporte, avec le vrai compte du jour. */
  gain: string;
  variant?: "section" | "lignes" | "colonnes" | "liste" | "blocs";
  labels?: string[];
  columns?: string[];
  count?: number;
  /** Variante blocs : une carte par bloc réservé (titre, libellés des lignes ou nombre de boules « ? »). */
  blocks?: { title: string; rows?: string[]; balls?: number }[];
  method?: string;
  /** Retour après inscription, avec l'ancre de la section. */
  next?: string;
  feature?: string;
  niveau?: "compte" | "premium";
  compact?: boolean;
  id?: string;
  headingLevel?: 2 | 3 | 4;
  /** Libellé du bouton quand l'action est plus précise que « Créer mon compte gratuit » (ex. « Garder cette grille »). */
  ctaLabel?: string;
  /** Paramètres ajoutés au lien d'inscription (ex. numbers : grille enregistrée après l'inscription). */
  params?: Record<string, string>;
};

const VISIBLE = 0.5;

export function KmTease({
  source,
  title,
  gain,
  variant = "section",
  labels = [],
  columns = [],
  count = 3,
  blocks = [],
  method,
  next,
  feature,
  niveau = "compte",
  compact = false,
  id,
  headingLevel = 3,
  ctaLabel,
  params: extra,
}: KmTeaseProps) {
  const cta = sanitizeCtaSource(source);
  if (cta === null || cta !== source) throw new Error(`cta_source invalide pour l'encart : « ${source} ».`);
  if (next !== undefined && safeNextPath(next) === null) throw new Error(`Retour invalide pour l'encart : « ${next} ».`);

  const ref = useRef<HTMLElement>(null);
  const vu = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || vu.current) return;
    const marquer = () => {
      if (vu.current) return;
      vu.current = true;
      trackSignupCtaViewed(source, feature);
    };
    const IO = (globalThis as { IntersectionObserver?: typeof IntersectionObserver }).IntersectionObserver;
    if (!IO) {
      marquer();
      return;
    }
    const obs = new IO(
      (entries) => entries.forEach((e) => e.isIntersecting && e.intersectionRatio >= VISIBLE && (marquer(), obs.disconnect())),
      { threshold: VISIBLE },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [source, feature]);

  const premium = niveau === "premium";
  // Rappel compact dont le titre nomme déjà le niveau (« … avec un compte gratuit ») : pas d'étiquette en double.
  const tagInTitle = compact && title.toLowerCase().includes(premium ? "premium" : "compte gratuit");
  const hid = id ?? `tease-${source}`;
  const params = new URLSearchParams({ cta_source: cta });
  if (next) params.set("next", next);
  const query = params.toString().replace(/\+/g, "%20");
  // Paramètres propres au bouton (grille à enregistrer) : pas sur « Se connecter », la connexion ne les lit pas.
  const ctaParams = new URLSearchParams(params);
  Object.entries(extra ?? {}).forEach(([k, v]) => ctaParams.set(k, v));
  const ctaQuery = ctaParams.toString().replace(/\+/g, "%20");
  const Titre = `h${headingLevel}` as "h2" | "h3" | "h4";
  const vides = Array.from({ length: Math.max(0, count) }, (_, i) => i);
  const classes = ["km-tease", `km-tease--${variant}`, compact && "km-tease--compact", premium && "km-tease--premium"].filter(Boolean).join(" ");

  return (
    <section ref={ref} className={classes} id={hid} aria-labelledby={`${hid}-t`} data-km-tease="" data-cta-source={source} data-cta-feature={feature} data-niveau={niveau}>
      <div className="km-tease__h">
        <Titre className="km-tease__t" id={`${hid}-t`}>{title}</Titre>
        {!tagInTitle && <span className="km-tease__tag">{premium ? "Premium" : "Compte gratuit"}</span>}
      </div>
      {method && <p className="km-tease__m">{method}</p>}
      {!compact && variant === "blocs" && blocks.length > 0 && (
        <ul className="km-tease__blocks">
          {blocks.map((b) => (
            <li key={b.title}>
              <span className="km-tease__bt">{b.title}</span>
              {b.rows && b.rows.length > 0 ? (
                <ul className="km-tease__kv" aria-hidden="true" data-nosnippet="">{b.rows.map((r) => <li key={r}><span>{r}</span><span className="km-tease__bar" /></li>)}</ul>
              ) : (b.balls ?? 0) > 0 ? (
                <div className="km-balls" aria-hidden="true" data-nosnippet="">{Array.from({ length: b.balls ?? 0 }, (_, i) => <span key={i} className="km-b km-b--sm km-tease__ball">?</span>)}</div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {!compact && variant !== "blocs" && vides.length > 0 && (variant === "colonnes" ? (
        <div className="km-tease__shape km-tease__scroll">
          <table className="km-tease__grid">
            <thead><tr>{columns.map((c) => <th key={c} scope="col">{c}</th>)}</tr></thead>
            <tbody aria-hidden="true" data-nosnippet="">
              {vides.map((i) => <tr key={i}>{columns.map((c) => <td key={c}><span className="km-tease__bar" /></td>)}</tr>)}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="km-tease__shape" aria-hidden="true" data-nosnippet="">
          {variant === "liste" && <div className="km-balls">{vides.map((i) => <span key={i} className="km-b km-b--sm km-tease__ball">?</span>)}</div>}
          {variant === "lignes" && <ul className="km-tease__rows">{vides.map((i) => <li key={i}><span className="km-b km-b--sm km-tease__ball">?</span><span className="km-tease__bar" /></li>)}</ul>}
          {variant === "section" && <div className="km-tease__lines">{vides.map((i) => <span key={i} className="km-tease__bar" />)}</div>}
        </div>
      ))}
      {labels.length > 0 && <ul className="km-tease__labels">{labels.map((l) => <li key={l}>{l}</li>)}</ul>}
      <div className="km-tease__go" data-nosnippet="">
        <p className="km-tease__gain">{gain}</p>
        <p className="km-tease__act">
          <a className="km-btn km-tease__cta" href={`${premium ? "/tarifs/" : "/inscription/"}?${ctaQuery}`} data-km-tease-cta="" onClick={() => trackSignupCtaClicked(source, feature)}>
            {ctaLabel ?? (premium ? "Découvrir Premium" : "Créer mon compte gratuit")}
          </a>
          {/* L'argument « sans carte bancaire » suit le bouton ; « Se connecter » vient en dernier. */}
          {!premium && <span className="km-tease__note">Sans carte bancaire.</span>}
          {!premium && <span className="km-tease__login">Déjà inscrit ? <a href={`/login/?${query}`}>Se connecter</a></span>}
        </p>
      </div>
    </section>
  );
}
